// Admin: change a vendor's password (Admin > Vendors and Admin > Marketing). The admin types one
// or generates one; it stays visible to admins as a temporary password until the vendor sets their own.
import React, { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Alert, Stack, Typography, RadioGroup, Radio,
  FormControlLabel, IconButton, InputAdornment, Paper,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { SET_VENDOR_PASSWORD_MUTATION, VENDOR_CREDENTIALS_QUERY } from '../graphql/queries';

const copy = (text) => { try { navigator.clipboard.writeText(text); } catch (e) { /* ignore */ } };

export default function VendorPasswordDialog({ vendorUserId, name, email, onClose, onDone }) {
  const [mode, setMode] = useState('generate');
  const [password, setPassword] = useState('');
  const [visible, setVisible] = useState(false);
  const [save, { data, loading, error }] = useMutation(SET_VENDOR_PASSWORD_MUTATION);
  const [lookUp, { data: current, loading: looking, error: lookError }] = useLazyQuery(VENDOR_CREDENTIALS_QUERY, { fetchPolicy: 'network-only' });
  const result = data?.setVendorPassword;
  const cur = current?.vendorCredentials;
  const weak = mode === 'type' && password && (password.length < 8 || !/[A-Za-z]/.test(password) || !/\d/.test(password));

  const go = async () => {
    await save({ variables: { vendorUserId, ...(mode === 'generate' ? { generate: true } : { password }) } });
    if (onDone) onDone();
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Change password{name ? `: ${name}` : ''}</DialogTitle>
      <DialogContent>
        {!result ? (
          <Stack spacing={2} mt={0.5}>
            <Typography variant="body2">User ID: <b>{email}</b></Typography>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              {!cur ? (
                <Button size="small" disabled={looking} onClick={() => lookUp({ variables: { vendorUserId } })}>
                  {looking ? 'Checking…' : 'Show the current temporary password'}
                </Button>
              ) : cur.status === 'temporary' ? (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="body2">Current temporary password: <b style={{ fontFamily: 'monospace' }}>{cur.tempPassword}</b></Typography>
                  <IconButton size="small" onClick={() => copy(`User ID: ${cur.email}\nTemporary password: ${cur.tempPassword}`)}><ContentCopyIcon fontSize="inherit" /></IconButton>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {cur.status === 'changed' ? 'The vendor has set their own password; it cannot be shown.' : 'No temporary password on record: the vendor uses their own password.'}
                </Typography>
              )}
              {lookError && <Alert severity="error" sx={{ mt: 1 }}>{lookError.message}</Alert>}
            </Paper>
            <RadioGroup value={mode} onChange={e => setMode(e.target.value)}>
              <FormControlLabel value="generate" control={<Radio />} label="Generate a new temporary password" />
              <FormControlLabel value="type" control={<Radio />} label="Type a password" />
            </RadioGroup>
            {mode === 'type' && (
              <TextField label="New password" type={visible ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} autoFocus
                error={Boolean(weak)} helperText="At least 8 characters, with letters and numbers"
                InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setVisible(v => !v)}>{visible ? <VisibilityOffIcon /> : <VisibilityIcon />}</IconButton></InputAdornment> }} />
            )}
            <Alert severity="info">
              The vendor's current password stops working straight away. The new one is shown to admins (here and on the Marketing lead) until the
              vendor changes it. Nothing is sent to the vendor: share it with them yourself.
            </Alert>
            {error && <Alert severity="error">{error.message}</Alert>}
          </Stack>
        ) : (
          <Alert severity="success" sx={{ mt: 0.5 }}>
            Password changed.<br />
            User ID: <b>{result.email}</b><br />
            Password: <b style={{ fontFamily: 'monospace', fontSize: 16 }}>{result.tempPassword}</b>
            <IconButton size="small" onClick={() => copy(`User ID: ${result.email}\nPassword: ${result.tempPassword}`)}><ContentCopyIcon fontSize="inherit" /></IconButton>
            <br />Ask the vendor to set their own password after signing in ("Forgot password" on the sign-in page).
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{result ? 'Done' : 'Cancel'}</Button>
        {!result && (
          <Button variant="contained" disabled={loading || (mode === 'type' && (!password || weak))} onClick={go}>
            {loading ? 'Saving…' : 'Change password'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
